import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PolicyIcon from '@mui/icons-material/Policy';
import {
  ROOMNEXA_TERMS_LAST_UPDATED,
  ROOMNEXA_TERMS_SECTION_CONTENT,
  ROOMNEXA_TERMS_TOC,
} from './legalTermsContent';
import { LOGIN_ACCENT as ACCENT } from './loginDarkTheme';
import './authPages.css';

export default function LoginTermsDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeSection, setActiveSection] = useState(ROOMNEXA_TERMS_TOC[0]?.id || 'intro');

  const activeLabel = useMemo(
    () => ROOMNEXA_TERMS_TOC.find((item) => item.id === activeSection)?.label || 'Terms',
    [activeSection],
  );

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    window.requestAnimationFrame(() => {
      document.getElementById(`login-terms-${sectionId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        className: 'login-terms-dialog',
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: fullScreen ? '100%' : '88vh',
          bgcolor: '#0b1120',
          border: fullScreen ? 'none' : '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 24px 90px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(2,6,23,0.78)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          },
        },
      }}
    >
      <DialogTitle
        className="login-terms-header"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: '#f8fffc',
          borderBottom: '1px solid rgba(15,23,42,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(ACCENT, 0.14),
              color: ACCENT,
              flexShrink: 0,
            }}
          >
            <PolicyIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" variant="h6" className="login-terms-title" sx={{ color: '#1f2937', fontWeight: 800, lineHeight: 1.2, display: 'block' }}>
              RoomNexa Terms & Conditions
            </Typography>
            <Typography component="span" variant="caption" className="login-terms-updated" sx={{ color: '#64748b', fontWeight: 700, display: 'block' }}>
              Last updated: {ROOMNEXA_TERMS_LAST_UPDATED}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="Close terms and conditions"
          onClick={onClose}
          sx={{
            color: '#64748b',
            '&:hover': { color: '#0f172a', bgcolor: 'rgba(15,23,42,0.08)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '230px 1fr' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            borderRight: { xs: 'none', md: '1px solid rgba(255,255,255,0.1)' },
            borderBottom: { xs: '1px solid rgba(255,255,255,0.1)', md: 'none' },
            bgcolor: 'rgba(15,23,42,0.68)',
            overflowX: { xs: 'auto', md: 'hidden' },
            overflowY: { xs: 'hidden', md: 'auto' },
            p: { xs: 1.25, md: 1.5 },
          }}
        >
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            spacing={1}
            sx={{
              minWidth: { xs: 'max-content', md: 0 },
              pr: { xs: 1, md: 0 },
            }}
          >
            {ROOMNEXA_TERMS_TOC.map((item) => {
              const selected = item.id === activeSection;
              return (
                <Button
                  key={item.id}
                  size="small"
                  onClick={() => handleSectionClick(item.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 1.4,
                    py: 0.9,
                    color: selected ? '#031712' : 'rgba(226,232,240,0.78)',
                    bgcolor: selected ? ACCENT : 'transparent',
                    fontWeight: selected ? 800 : 600,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: selected ? ACCENT : 'rgba(76,225,182,0.1)',
                      color: selected ? '#031712' : '#fff',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </Box>

        <Box
          className="login-terms-content"
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            bgcolor: '#f8fafc',
            color: '#1f2937',
            overflowY: 'auto',
            maxHeight: fullScreen ? 'calc(100vh - 155px)' : 'calc(88vh - 138px)',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
          }}
        >
          <Typography
            component="span"
            className="login-terms-active-label"
            sx={{
              color: ACCENT,
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            {activeLabel}
          </Typography>
          {ROOMNEXA_TERMS_TOC.map((item) => (
            <TermsSection key={item.id} id={item.id} title={item.label} />
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          bgcolor: 'rgba(15,23,42,0.82)',
        }}
      >
        <Typography component="span" variant="caption" className="login-terms-footer-note" sx={{ color: 'rgba(226,232,240,0.78)', flex: 1, display: 'block' }}>
          Please review these terms before continuing to RoomNexa.
        </Typography>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: ACCENT,
            color: '#031712',
            fontWeight: 800,
            textTransform: 'none',
            '&:hover': { bgcolor: '#37d5a5' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TermsSection({ id, title }) {
  const section = ROOMNEXA_TERMS_SECTION_CONTENT[id];
  if (!section) return null;

  return (
    <Box id={`login-terms-${id}`} sx={{ scrollMarginTop: 16, mb: 3 }}>
      <Typography component="span" variant="h6" className="login-terms-section-title" sx={{ fontWeight: 800, color: '#1f2937', mb: 1, display: 'block' }}>
        {title}
      </Typography>
      {section.paragraphs.map((paragraph) => (
        <Typography key={paragraph.slice(0, 60)} component="span" className="login-terms-body" sx={{ color: '#374151', lineHeight: 1.72, mb: 1.2, fontWeight: 500, display: 'block' }}>
          {paragraph}
        </Typography>
      ))}
      {section.bullets?.length ? (
        <Box component="ul" className="login-terms-list" sx={{ pl: 2.2, m: 0, display: 'grid', gap: 0.8, color: '#374151' }}>
          {section.bullets.map((item) => (
            <Box key={item} component="li" sx={{ lineHeight: 1.65, fontWeight: 600 }}>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
